import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Calendar, Sparkles, Filter } from "lucide-react";
import { API } from "../App";
import TopNav from "../components/TopNav";
import TrustStars from "../components/TrustStars";
import { countryToFlag } from "../lib/countryFlag";

const formatKickoff = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return "";
  }
};

const dayKey = (iso) => {
  if (!iso) return "unknown";
  try {
    const d = new Date(iso);
    return d.getUTCDay() === 6 ? "sat" : d.getUTCDay() === 0 ? "sun" : "other";
  } catch {
    return "other";
  }
};

const formatDayLabel = (dateStr) => {
  if (!dateStr) return "";
  try {
    const d = new Date(`${dateStr}T00:00:00Z`);
    return d.toLocaleDateString(undefined, {
      weekday: "long",
      day: "numeric",
      month: "long",
    });
  } catch {
    return dateStr;
  }
};

const WeekendPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [minTrust, setMinTrust] = useState(3);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data: payload } = await axios.get(
          `${API}/weekend/big-slate?min_trust=${minTrust}&limit=50`,
        );
        if (!cancelled) setData(payload);
      } catch (_) {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [minTrust]);

  const picks = data?.picks || [];
  const satPicks = picks.filter((p) => dayKey(p.kickoff) === "sat");
  const sunPicks = picks.filter((p) => dayKey(p.kickoff) === "sun");

  return (
    <div className="min-h-screen bg-background">
      <TopNav />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="pointer-events-none absolute -top-24 -right-16 h-80 w-80 rounded-full bg-amber-400/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-emerald-500/15 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
            data-testid="weekend-back-home"
          >
            <ArrowLeft size={14} />
            Back to homepage
          </button>
          <div className="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-[0.16em] uppercase text-amber-600 dark:text-amber-400 mb-3">
            <Sparkles size={14} className="fill-amber-400 text-amber-400" />
            <span>The weekend big slate</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
            Every elite weekend pick,
            <br />
            <span className="text-amber-600 dark:text-amber-400">ranked by Trust Score.</span>
          </h1>
          <p className="mt-4 text-base text-muted-foreground max-w-2xl">
            Curated picks across UCL, Premier League, La Liga, Serie A, Bundesliga,
            Ligue 1 and Liga Portugal — filtered by the model's self-learning
            Trust Score so only calibrated markets surface.
          </p>
          {data && (
            <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <Calendar size={14} />
                <span data-testid="weekend-hero-range">
                  {formatDayLabel(data.weekend?.sat)} – {formatDayLabel(data.weekend?.sun)}
                </span>
              </span>
              <span className="opacity-50">·</span>
              <span>{data.fixtures_in_window} elite fixtures</span>
              <span className="opacity-50">·</span>
              <span>{data.leagues?.length || 0} leagues</span>
              <span className="opacity-50">·</span>
              <span className="font-semibold text-foreground">{data.count} picks surfaced</span>
            </div>
          )}

          {/* Trust filter */}
          <div className="mt-8 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-[0.08em] uppercase text-muted-foreground mr-2">
              <Filter size={12} />
              Trust floor
            </span>
            {[3, 4, 5].map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setMinTrust(t)}
                className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                  minTrust === t
                    ? "border-amber-400 bg-amber-400/10 text-amber-700 dark:text-amber-400"
                    : "border-border bg-card hover:bg-muted text-muted-foreground"
                }`}
                data-testid={`weekend-filter-trust-${t}`}
              >
                {t}★ and up
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Picks */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {loading ? (
          <div className="text-center py-16 text-muted-foreground" data-testid="weekend-loading">
            Loading weekend slate…
          </div>
        ) : picks.length === 0 ? (
          <div className="text-center py-16" data-testid="weekend-empty">
            <p className="text-lg font-semibold">No picks meet the {minTrust}★ trust floor this weekend.</p>
            <p className="mt-2 text-sm text-muted-foreground">Try a lower trust floor, or check back as predictions mature.</p>
          </div>
        ) : (
          <div className="space-y-10">
            {[
              { label: formatDayLabel(data?.weekend?.sat), picks: satPicks, key: "sat" },
              { label: formatDayLabel(data?.weekend?.sun), picks: sunPicks, key: "sun" },
            ]
              .filter((d) => d.picks.length > 0)
              .map((day) => (
                <section key={day.key} aria-labelledby={`day-${day.key}`} data-testid={`weekend-day-${day.key}`}>
                  <div className="flex items-baseline justify-between mb-4">
                    <h2 id={`day-${day.key}`} className="font-display text-2xl sm:text-3xl font-bold tracking-tight">
                      {day.label}
                    </h2>
                    <span className="eyebrow">{day.picks.length} picks</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {day.picks.map((p, idx) => {
                      const m = p.match;
                      const mk = p.market;
                      return (
                        <button
                          key={`${p.fixture_id}-${idx}`}
                          type="button"
                          onClick={() => navigate(`/match/${p.fixture_id}`)}
                          className="group relative text-left rounded-2xl border border-border bg-card hover:border-amber-400/50 transition-all p-4"
                          data-testid={`weekend-full-pick-${p.fixture_id}-${idx}`}
                        >
                          <div className="flex items-center justify-between text-[10px] font-bold tracking-[0.08em] uppercase text-muted-foreground mb-2">
                            <span className="truncate">
                              {countryToFlag(m.league?.country)} {m.league?.name}
                            </span>
                            <span className="font-tabular shrink-0">{formatKickoff(p.kickoff)}</span>
                          </div>
                          <div className="font-display text-sm font-semibold leading-snug mb-3 line-clamp-2">
                            {m.home_team?.name}{" "}
                            <span className="text-muted-foreground font-normal">vs</span>{" "}
                            {m.away_team?.name}
                          </div>
                          <div className="flex items-center justify-between gap-2 pt-2 border-t border-border/50">
                            <div className="min-w-0">
                              <div className="text-[10px] font-bold tracking-[0.08em] uppercase text-muted-foreground truncate">
                                {mk.market_type}
                              </div>
                              <div className="text-sm font-semibold truncate">{mk.prediction}</div>
                            </div>
                            <div className="flex flex-col items-end gap-0.5 shrink-0">
                              <span className="font-tabular font-bold text-base text-amber-700 dark:text-amber-400">
                                {Math.round(mk.confidence)}%
                              </span>
                              <TrustStars
                                score={mk.trust_score}
                                components={mk.trust_components}
                                size="xs"
                                testId={`weekend-full-trust-${p.fixture_id}-${idx}`}
                              />
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </section>
              ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default WeekendPage;
